/**
 * Section Codes promo — codes des propriétaires (administrateurs).
 * GET/POST /api/admin/owner-promos, PUT /api/admin/owner-promos/:id,
 * GET /api/admin/owner-promos/:id/uses.
 *
 * Un code réduit ce que le propriétaire reverse à Dingo sur les frais choisis
 * (commission, service, protection). Le locataire n'en voit rien. La remise est
 * figée sur chaque location ; modifier un code ne touche pas celles déjà remisées.
 */

export type PromoFeeCode = 'owner_commission' | 'renter_service' | 'renter_protection';
export type PromoDiscountType = 'percentage' | 'fixed' | 'free';
export type PromoEligibility = 'all' | 'new' | 'selected';
export type PromoState = 'active' | 'scheduled' | 'ended' | 'full' | 'disabled';

export interface OwnerPromo {
  id_owner_promo: string;
  code: string;
  description: string | null;
  fee_codes: PromoFeeCode[];
  discount_type: PromoDiscountType;
  discount_value: number;
  per_owner_rentals: number | null;
  max_owners: number | null;
  starts_at: string | null;
  ends_at: string | null;
  eligibility: PromoEligibility;
  allowed_owner_ids: string[];
  is_active: boolean;
  created_at: string;
  owners_count: number;
  rentals_count: number;
  discount_granted: number;
  state: PromoState;
}

export interface OwnerPromoUse {
  owner_id: string;
  name: string;
  activated_at: string;
  rentals: number;
  saved: number;
}

export interface OwnerPromoList {
  codes: OwnerPromo[];
  kpis: { discount_this_month: number; rentals_this_month: number; owners: number };
}

export type OwnerPromoInput = Partial<Pick<OwnerPromo,
  'code' | 'description' | 'fee_codes' | 'discount_type' | 'discount_value' | 'per_owner_rentals' |
  'max_owners' | 'starts_at' | 'ends_at' | 'eligibility' | 'allowed_owner_ids' | 'is_active'>>;

const base = () => `${process.env.REACT_APP_API_URL}api/admin/owner-promos`;

async function call<T>(token: string, path: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(`${base()}${path}`, {
    ...init,
    headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}`, ...(init.headers || {}) }
  });
  const data = await res.json().catch(() => ({}));
  if (!res.ok) throw new Error(data?.error || `Erreur ${res.status}`);
  return data as T;
}

export const getOwnerPromos = (token: string) => call<OwnerPromoList>(token, '');

export const createOwnerPromo = async (token: string, input: OwnerPromoInput): Promise<OwnerPromo> =>
  (await call<{ promo: OwnerPromo }>(token, '', { method: 'POST', body: JSON.stringify(input) })).promo;

export const updateOwnerPromo = async (token: string, id: string, input: OwnerPromoInput): Promise<OwnerPromo> =>
  (await call<{ promo: OwnerPromo }>(token, `/${id}`, { method: 'PUT', body: JSON.stringify(input) })).promo;

export const getOwnerPromoUses = async (token: string, id: string): Promise<OwnerPromoUse[]> =>
  (await call<{ uses: OwnerPromoUse[] }>(token, `/${id}/uses`)).uses;
